import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { VehicleSocket } from "../../../../../../../store/admin/vehiclesSocketStore";
import {
  EVENT_CODE_KEYS,
  EVENT_CODE_LABELS_ES,
} from "../../../../../../../store/admin/deviceEventsStore";
import { getCustomSelectStyles } from "../../../../../../../Layouts/admin/shared/theme/selectStyles";
import { activeThemeId } from "../../../../../../../config/theme";
import api from "../../../../../../../utils/api";
import { getError } from "../../../../../../../utils/getError";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

const CENTRAL_GPS_ONLY_EVENT_CODES = new Set<number>([44, 45, 46, 47]);

interface CreateEventModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface EventOption {
  value: number;
  label: string;
  code: string;
}

interface EventFormData {
  eventCode: number;
  eventType: string;
  customEvent: string;
}

export default function CreateEventModal({
  vehicle,
  isDarkMode,
  onClose,
  onSuccess,
}: CreateEventModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const [formData, setFormData] = useState<EventFormData>({
    eventCode: 0,
    eventType: "",
    customEvent: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    setCurrentTimestamp(Date.now());

    const intervalId = setInterval(() => {
      setCurrentTimestamp(Date.now());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const formatTimestampForDisplay = useCallback((timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
  }, []);

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-[#252525]" : "bg-white";

  const isCentralGps = activeThemeId === "central-gps";
  const eventOptions = useMemo<EventOption[]>(() => {
    const options: EventOption[] = [];

    Object.entries(EVENT_CODE_KEYS).forEach(([code, key]) => {
      const codeNum = parseInt(code);
      if (!isCentralGps && CENTRAL_GPS_ONLY_EVENT_CODES.has(codeNum)) return;
      const label = EVENT_CODE_LABELS_ES[code] || key;
      options.push({
        value: codeNum,
        label: `${label} (#${code})`,
        code: key,
      });
    });

    return options;
  }, [isCentralGps]);

  const selectedOption = useMemo(() => {
    return eventOptions.find((opt) => opt.value === formData.eventCode) || null;
  }, [eventOptions, formData.eventCode]);

  const isOtherEvent = formData.eventCode === 43;

  const handleEventChange = (option: EventOption | null) => {
    if (!option) {
      setFormData({
        eventCode: 0,
        eventType: "",
        customEvent: "",
      });
      return;
    }

    setFormData({
      ...formData,
      eventCode: option.value,
      eventType: option.code,
      customEvent: option.value !== 43 ? "" : formData.customEvent,
    });
  };

  const handleSubmit = async () => {
    if (!formData.eventCode) return;
    if (isOtherEvent && !formData.customEvent.trim()) return;

    const imei = vehicle.imei || vehicle.id;
    if (!imei) {
      setError(t("createEventModal.imeiNotAvailable"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const eventType = isOtherEvent
        ? formData.customEvent.trim()
        : formData.eventType;

      const timestampToSend = Date.now();

      const requestBody: Record<string, any> = {
        imei,
        event_code: formData.eventCode,
        event_type: eventType,
        timeStamp: timestampToSend,
      };

      if (vehicle.latitude != null) {
        requestBody.lat = vehicle.latitude;
      }
      if (vehicle.longitude != null) {
        requestBody.lon = vehicle.longitude;
      }

      if (vehicle.ignition != null) {
        requestBody.engine_status = vehicle.ignition ? 1 : 0;
      }

      if (vehicle.odometer != null) {
        requestBody.odo_km = vehicle.odometer;
      }

      await api.post("/ingestEvent", requestBody);

      setShowSuccess(true);
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = getError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    onClose();
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div
          className={`${bgColor} rounded-xl max-w-sm w-full p-8 border ${borderColor} shadow-2xl`}
        >
          <div className="flex flex-col items-center justify-center py-6">
            {/* Icono de check */}
            <div className="w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-black"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            {/* Título */}
            <h3 className={`font-bold text-xl ${textColor} mb-2`}>
              {t("createEventModal.eventSentSuccess")}
            </h3>

            {/* Subtítulo */}
            <p className={`text-center ${textSecondary} text-sm mb-6`}>
              {t("createEventModal.eventRegistered")}
            </p>

            {/* Botón continuar */}
            <button
              onClick={handleContinue}
              className="px-8 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors"
            >
              {t("createEventModal.continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-md w-full overflow-hidden border ${borderColor} shadow-2xl`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-notification-3-line text-black text-lg"></i>
            </div>
            <h2 className={`font-semibold text-lg ${textColor}`}>
              {t("createEventModal.title")}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`p-2 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            } rounded-lg transition-colors disabled:opacity-50`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info del vehículo */}
          <div className={`p-3 rounded-lg ${isDarkMode ? "bg-[#252525]" : "bg-gray-50"} mb-4`}>
            <div className="flex items-center justify-between text-sm">
              <span className={textSecondary}>{t("createEventModal.vehicleLabel")}:</span>
              <span className={`font-medium ${textColor}`}>
                {vehicle.vehicle.name || vehicle.fullId}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className={textSecondary}>{t("createEventModal.imei")}:</span>
              <span className={`font-medium ${textColor}`}>
                {vehicle.imei || vehicle.id || t("createEventModal.notAvailable")}
              </span>
            </div>
            {vehicle.latitude && vehicle.longitude && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className={textSecondary}>{t("createEventModal.location")}:</span>
                <span className={`font-medium ${textColor}`}>
                  {vehicle.latitude.toFixed(5)}, {vehicle.longitude.toFixed(5)}
                </span>
              </div>
            )}
          </div>

          {/* Fecha y hora del evento */}
          <div className={`p-3 rounded-lg border-2 border-primary-500/30 ${isDarkMode ? "bg-primary-500/10" : "bg-primary-50"} mb-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className={`ri-time-line text-primary-500`}></i>
                <span className={`text-sm ${textSecondary}`}>
                  {t("createEventModal.eventDateTime")}:
                </span>
              </div>
              <span className={`font-medium ${textColor}`}>
                {formatTimestampForDisplay(currentTimestamp)}
              </span>
            </div>
            <p className={`text-xs ${textSecondary} mt-1`}>
              <i className="ri-refresh-line mr-1"></i>
              {t("createEventModal.timestampUpdatesEveryMinute")}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Select de evento con React Select */}
          <div className="mb-4">
            <label className={`block text-sm ${textColor} mb-2`}>
              <span className="text-red-500">*</span> {t("createEventModal.eventType")}
            </label>
            <Select<EventOption>
              value={selectedOption}
              onChange={handleEventChange}
              options={eventOptions}
              styles={getCustomSelectStyles(isDarkMode) as any}
              placeholder={t("createEventModal.selectEvent")}
              isSearchable
              isDisabled={isLoading}
              noOptionsMessage={() => t("createEventModal.noEventsAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Input para nombre del evento personalizado cuando es "Otro" */}
          {isOtherEvent && (
            <div className="mb-4">
              <label className={`block text-sm ${textColor} mb-2`}>
                <span className="text-red-500">*</span> {t("createEventModal.eventName")}
              </label>
              <input
                type="text"
                value={formData.customEvent}
                onChange={(e) =>
                  setFormData({ ...formData, customEvent: e.target.value })
                }
                disabled={isLoading}
                placeholder={t("createEventModal.eventNamePlaceholder")}
                className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50`}
              />
            </div>
          )}

          {/* Info adicional */}
          <div className={`p-3 rounded-lg border ${borderColor} mb-6`}>
            <p className={`text-xs ${textSecondary}`}>
              <i className="ri-information-line mr-1"></i>
              {t("createEventModal.eventInfo")}
            </p>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={`px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50`}
            >
              {t("createEventModal.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                !formData.eventCode ||
                (isOtherEvent && !formData.customEvent.trim()) ||
                isLoading
              }
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
              )}
              {isLoading ? t("createEventModal.sending") : t("createEventModal.sendEvent")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
